import experiment from 'experiment';
import * as stateManagement from '#packages/stateManagement';
import { PremiumDataViewRetrieverServer } from '@wix/ambassador-premium-data-view-retriever-server/http';
import {
  PremiumAssetType,
  type PremiumAsset,
  ServiceStatus,
} from '@wix/ambassador-premium-data-view-retriever-server/types';
import { BasePublicApi } from '#packages/apilib';
import { ErrorReporter } from '@wix/editor-error-reporter';
import type { StudioMigrationManagerScope } from './studioMigrationManagerScope';

async function hasStudioPremium(
  scope: StudioMigrationManagerScope,
): Promise<boolean> {
  const { editorAPI } = scope;
  const metaSiteId = editorAPI.dsRead.generalInfo.getMetaSiteId();

  try {
    const UserPremiumAssetsServiceV1 = PremiumDataViewRetrieverServer(
      '/_api/premium-assets/',
      { ignoredProtoHttpUrlPart: '/api' },
    ).UserPremiumAssetsServiceV1();

    const response = await UserPremiumAssetsServiceV1().listUserPremiumAssets({
      filter: {
        metaSite: {
          metaSiteIds: [metaSiteId],
        },
        serviceStatus: {
          serviceStatuses: [ServiceStatus.ACTIVE],
        },
        assetType: {
          assetTypes: [PremiumAssetType.PLAN],
        },
      },
    });

    const hasStudioPremiumResult = response?.assets?.some(
      (asset: PremiumAsset) => {
        return asset.plansSpecificData?.planCapabilities?.compatibleWithStudio;
      },
    );

    editorAPI.store.dispatch(
      stateManagement.savePublish.actions.setIsStudioPremium(
        hasStudioPremiumResult,
      ),
    );
    return hasStudioPremiumResult;
  } catch (error) {
    ErrorReporter.captureException(error, {
      tags: {
        premium: 'hasStudioPremium',
      },
    });
    console.error(error);
    return false;
  }
}

const shouldBlockPublishForStudioMigration = (
  scope: StudioMigrationManagerScope,
): boolean => {
  const { editorAPI } = scope;

  const isClassicToStudioExperimentEnabled = experiment.isOpen(
    'specs.responsive-editor.classicToStudio',
  );
  if (!isClassicToStudioExperimentEnabled) {
    return false;
  }

  const state = editorAPI.store.getState();
  const isStudioPremiumPlan =
    stateManagement.savePublish.selectors.getIsStudioPremium(state);
  const hasBranches =
    stateManagement.branches.selectors.getDsHasBranches(state);

  return isStudioPremiumPlan && hasBranches;
};

export class StudioMigrationManagerApi extends BasePublicApi<StudioMigrationManagerScope> {
  hasStudioPremium = this.bindScope(hasStudioPremium);
  shouldBlockPublishForStudioMigration = this.bindScope(
    shouldBlockPublishForStudioMigration,
  );
}

export const initializeStudioPremium = async (
  scope: StudioMigrationManagerScope,
): Promise<void> => {
  const isExperimentOpen = experiment.isOpen(
    'specs.responsive-editor.classicToStudio',
  );

  if (!isExperimentOpen) {
    return;
  }

  await hasStudioPremium(scope);
};
